// Smart forms — the form engine, shared word for word by the server (zofino-strapi src/modules/forms/engine.ts)
// and the builder / public page (zofino-react src/modules/forms/engine.js), so a preview and the server always agree.
// Keep the two copies identical (no imports, no types); both repos test it. docs/tasks/37-smart-forms.md.
// Locale-neutral: no user-facing text here. Labels, validation messages and publish-report lines are codes that
// the UI translates (forms.field-type.*, forms.operator.*, forms.customer-field.*, forms.validation.*, forms.report.*).
// @ts-nocheck -- plain JavaScript on purpose (the React copy is the same file)
// Pure functions only: field types, value normalization, the condition tree, in-fill rules (show / hide / required /
// end early), answer validation, scores, the chosen ending, safe redirect URLs, template variables and the
// publish-readiness report. No eval, no user code — a rule is a JSON condition tree with typed operators.

export const SCHEMA_VERSION = 2;
export const MAX_RULE_DEPTH = 3;

export const FIELD_TYPES = {
  short_text: {answer: "text"},
  long_text: {answer: "text"},
  email: {answer: "text"},
  phone: {answer: "text"},
  number: {answer: "number"},
  amount: {answer: "number"},
  date: {answer: "date"},
  datetime: {answer: "date"},
  single_choice: {answer: "choice", options: true},
  dropdown: {answer: "choice", options: true},
  multi_choice: {answer: "multi", options: true},
  yes_no: {answer: "choice"},
  rating: {answer: "number"},
  file: {answer: "file"},
  consent: {answer: "consent"},
  hidden: {answer: "text"},
  heading: {answer: null},
  paragraph: {answer: null},
  divider: {answer: null},
};

export const OPERATORS = {
  text: ["eq", "neq", "contains", "not_contains", "starts_with", "empty", "not_empty"],
  number: ["eq", "neq", "gt", "gte", "lt", "lte", "between", "empty", "not_empty"],
  choice: ["is", "is_not", "empty", "not_empty"],
  multi: ["includes_any", "includes_all", "includes_none", "empty", "not_empty"],
  date: ["before", "after", "between", "within_next_days", "within_past_days", "empty", "not_empty"],
  consent: ["is", "empty", "not_empty"],
  file: ["empty", "not_empty"],
  identity: ["found", "not_found", "conflict"],
  system: ["is", "is_not"],
  score: ["gt", "gte", "lt", "lte", "between", "band_is"],
};

export const IN_FILL_ACTIONS = ["show_field", "hide_field", "set_required", "set_optional", "end_form"];
export const POST_SUBMIT_ACTIONS = ["create_task", "notify", "require_review", "update_customer"];
export const MAPPING_POLICIES = ["fill_if_empty", "replace", "append_unique", "timeline_only", "keep_latest", "require_approval", "require_identity_match"];
// the customer fields a question may fill (the CRM's own field names, as the customer form of the app sends them)
export const CUSTOMER_FIELDS = ["displayName", "firstName", "lastName", "companyName", "jobTitle", "primaryPhone", "primaryEmail", "website", "address", "description"];
export const IDENTITY_FIELDS = ["primaryPhone", "primaryEmail"];
export const TEMPLATE_VARIABLES = ["form.title", "submission.id", "submitted_at", "respondent.name", "customer.id", "customer.company_name"];

const isAnswerable = (field) => Boolean(field && FIELD_TYPES[field.type] && FIELD_TYPES[field.type].answer);
export const answerKind = (field) => (field && FIELD_TYPES[field.type] ? FIELD_TYPES[field.type].answer : null);

// ---------------------------------------------------------------- normalization
const PERSIAN = "\u06F0\u06F1\u06F2\u06F3\u06F4\u06F5\u06F6\u06F7\u06F8\u06F9";
const ARABIC = "\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669";
export function latinDigits(value) {
  return String(value === null || value === undefined ? "" : value).replace(/[\u06F0-\u06F9\u0660-\u0669]/g, (char) => {
    const persian = PERSIAN.indexOf(char);
    return String(persian >= 0 ? persian : ARABIC.indexOf(char));
  });
}

// Iranian mobile numbers to +989…; anything else keeps its digits with a leading +
export function normalizePhone(value) {
  let digits = latinDigits(value).replace(/[\s\-().]/g, "");
  if (!digits) return "";
  if (digits.startsWith("00")) digits = "+" + digits.slice(2);
  if (/^09\d{9}$/.test(digits)) return "+98" + digits.slice(1);
  if (/^9\d{9}$/.test(digits)) return "+98" + digits;
  if (/^989\d{9}$/.test(digits)) return "+" + digits;
  return digits;
}

export function normalizeEmail(value) {
  return String(value === null || value === undefined ? "" : value).trim().toLowerCase();
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function normalizeValue(field, raw) {
  if (raw === undefined || raw === null) return null;
  const kind = answerKind(field);
  if (field.type === "email") return normalizeEmail(raw) || null;
  if (field.type === "phone") return normalizePhone(raw) || null;
  if (kind === "number") {
    const cleaned = latinDigits(raw).replace(/[,\u066C\s]/g, "");
    if (cleaned === "") return null;
    const number = Number(cleaned);
    return Number.isFinite(number) ? number : NaN;
  }
  if (kind === "date") {
    const time = Date.parse(latinDigits(raw));
    return Number.isFinite(time) ? new Date(time).toISOString() : NaN;
  }
  if (kind === "multi") return (Array.isArray(raw) ? raw : [raw]).map(String).filter(Boolean);
  if (kind === "consent") return raw === true || raw === "true";
  if (kind === "file") return (Array.isArray(raw) ? raw : [raw]).filter(Boolean).map((item) => (typeof item === "object" ? item : {id: String(item)}));
  if (kind === "choice") return String(raw);
  const text = String(raw).trim();
  return text === "" ? null : text;
}

export const isEmptyValue = (value) => value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0) || value === false || (typeof value === "number" && Number.isNaN(value));

// ---------------------------------------------------------------- conditions
// A condition is {field, operator, value} (or {source: "identity" | "system" | "score", ...}); a group is
// {op: "and" | "or", conditions: [...]}. An empty answer makes a condition false, except «empty».
export function evaluateCondition(condition, answers, context) {
  if (!condition) return true;
  if (Array.isArray(condition.conditions)) return evaluateGroup(condition, answers, context, 1);
  const operator = condition.operator;
  if (condition.source === "identity") {
    const state = context && context.identityState;
    if (operator === "found") return state === "matched" || state === "manually_resolved";
    if (operator === "not_found") return state === "unmatched" || state === "created";
    if (operator === "conflict") return state === "conflict";
    return false;
  }
  if (condition.source === "system") {
    const actual = context && context.system ? context.system[condition.field] : undefined;
    return operator === "is_not" ? actual !== condition.value : actual === condition.value;
  }
  if (condition.source === "score") {
    const score = context && context.scores ? context.scores[condition.field] : null;
    if (!score) return false;
    if (operator === "band_is") return score.band === condition.value;
    return compareNumber(operator, score.normalized !== null && score.normalized !== undefined ? score.normalized : score.raw, condition.value, condition.valueTo);
  }
  const value = answers ? answers[condition.field] : undefined;
  if (operator === "empty") return isEmptyValue(value);
  if (operator === "not_empty") return !isEmptyValue(value);
  if (isEmptyValue(value)) return false;
  const expected = condition.value;
  switch (operator) {
    case "eq": return typeof value === "number" ? value === Number(latinDigits(expected)) : String(value).toLowerCase() === String(expected === null || expected === undefined ? "" : expected).toLowerCase();
    case "neq": return typeof value === "number" ? value !== Number(latinDigits(expected)) : String(value).toLowerCase() !== String(expected === null || expected === undefined ? "" : expected).toLowerCase();
    case "contains": return String(value).toLowerCase().includes(String(expected || "").toLowerCase());
    case "not_contains": return !String(value).toLowerCase().includes(String(expected || "").toLowerCase());
    case "starts_with": return String(value).toLowerCase().startsWith(String(expected || "").toLowerCase());
    case "is": return Array.isArray(value) ? value.includes(String(expected)) : String(value) === String(expected);
    case "is_not": return Array.isArray(value) ? !value.includes(String(expected)) : String(value) !== String(expected);
    case "includes_any": return asList(expected).some((item) => asList(value).includes(item));
    case "includes_all": return asList(expected).every((item) => asList(value).includes(item));
    case "includes_none": return !asList(expected).some((item) => asList(value).includes(item));
    case "before": return Date.parse(value) < Date.parse(expected);
    case "after": return Date.parse(value) > Date.parse(expected);
    case "within_next_days": {
      const now = context && context.now ? context.now : Date.now();
      const at = Date.parse(value);
      return at >= now && at <= now + Number(expected) * 86400000;
    }
    case "within_past_days": {
      const now = context && context.now ? context.now : Date.now();
      const at = Date.parse(value);
      return at <= now && at >= now - Number(expected) * 86400000;
    }
    default:
      return compareNumber(operator, value, expected, condition.valueTo);
  }
}

function asList(value) {
  return (Array.isArray(value) ? value : [value]).filter((item) => item !== null && item !== undefined).map(String);
}

function compareNumber(operator, actual, expected, upper) {
  if (operator === "between") {
    const range = Array.isArray(expected) ? expected : [expected, upper];
    if (typeof actual === "string" && Number.isNaN(Number(actual))) return Date.parse(actual) >= Date.parse(range[0]) && Date.parse(actual) <= Date.parse(range[1]);
    return Number(actual) >= Number(latinDigits(range[0])) && Number(actual) <= Number(latinDigits(range[1]));
  }
  const left = Number(actual);
  const right = Number(latinDigits(expected));
  if (!Number.isFinite(left) || !Number.isFinite(right)) return false;
  if (operator === "gt") return left > right;
  if (operator === "gte") return left >= right;
  if (operator === "lt") return left < right;
  if (operator === "lte") return left <= right;
  if (operator === "eq") return left === right;
  if (operator === "neq") return left !== right;
  return false;
}

function evaluateGroup(group, answers, context, depth) {
  const list = (group.conditions || []).filter(Boolean);
  if (!list.length) return true;
  const results = list.map((item) => (Array.isArray(item.conditions)
    ? (depth >= MAX_RULE_DEPTH ? false : evaluateGroup(item, answers, context, depth + 1))
    : evaluateCondition(item, answers, context)));
  return group.op === "or" ? results.some(Boolean) : results.every(Boolean);
}

export function conditionFields(condition, out = []) {
  if (!condition) return out;
  if (Array.isArray(condition.conditions)) condition.conditions.forEach((item) => conditionFields(item, out));
  else if (!condition.source && condition.field) out.push(condition.field);
  return out;
}

const byPriority = (list) => (list || []).map((item, index) => ({item, index}))
  .sort((a, b) => (Number(a.item.priority) || 0) - (Number(b.item.priority) || 0) || a.index - b.index)
  .map((entry) => entry.item);

// ---------------------------------------------------------------- in-fill state
// Which fields are visible / required right now, and whether a rule ended the form early. A field a rule shows starts
// hidden; a hidden field is never required and its value does not count (validation, scores, mapping).
export function formState(definition, answers, context) {
  const fields = (definition && definition.fields) || [];
  const rules = byPriority(((definition && definition.rules) || []).filter((rule) => rule && rule.enabled !== false));
  const shownByRule = new Set();
  rules.forEach((rule) => (rule.then || []).forEach((action) => { if (action.type === "show_field" && action.field) shownByRule.add(action.field); }));
  const visible = {};
  const required = {};
  fields.forEach((field) => {
    visible[field.key] = field.type !== "hidden" && !shownByRule.has(field.key);
    required[field.key] = Boolean(field.required) && isAnswerable(field) && field.type !== "hidden";
  });
  let ended = null;
  for (const rule of rules) {
    // a rule reading a field that is hidden right now reads it as empty
    const visibleAnswers = {};
    Object.keys(answers || {}).forEach((key) => { if (visible[key] !== false || isHiddenType(fields, key)) visibleAnswers[key] = answers[key]; });
    if (!evaluateCondition(rule.when, visibleAnswers, context)) continue;
    for (const action of rule.then || []) {
      if (action.type === "show_field") visible[action.field] = true;
      if (action.type === "hide_field") visible[action.field] = false;
      if (action.type === "set_required") required[action.field] = true;
      if (action.type === "set_optional") required[action.field] = false;
      if (action.type === "end_form" && !ended) ended = {ruleKey: rule.key, endingKey: action.endingKey || null, afterField: action.afterField || null};
    }
    if (rule.stopProcessing) break;
  }
  // an early end hides every question after the one it ends at
  if (ended && ended.afterField) {
    let passed = false;
    fields.forEach((field) => {
      if (passed) visible[field.key] = false;
      if (field.key === ended.afterField) passed = true;
    });
  }
  Object.keys(required).forEach((key) => { if (!visible[key]) required[key] = false; });
  return {visible, required, ended};
}

const isHiddenType = (fields, key) => fields.some((field) => field.key === key && field.type === "hidden");

// ---------------------------------------------------------------- validation of answers
// Only defined field keys are accepted; required means visible-and-required at the final state.
export function validateAnswers(definition, rawAnswers, context) {
  const fields = ((definition && definition.fields) || []).filter(isAnswerable);
  const known = new Set(fields.map((field) => field.key));
  const normalized = {};
  const fieldErrors = {};
  Object.keys(rawAnswers || {}).forEach((key) => {
    if (!known.has(key)) fieldErrors[key] = {code: "unknown_field"};
  });
  fields.forEach((field) => {
    const value = normalizeValue(field, rawAnswers ? rawAnswers[field.key] : undefined);
    if (value !== null && value !== undefined) normalized[field.key] = value;
  });
  const state = formState(definition, normalized, context);
  fields.forEach((field) => {
    if (!state.visible[field.key] && field.type !== "hidden") {
      delete normalized[field.key];
      return;
    }
    const value = normalized[field.key];
    const error = fieldError(field, value, state.required[field.key]);
    if (error) fieldErrors[field.key] = error;
  });
  return {answers: normalized, fieldErrors, state, valid: Object.keys(fieldErrors).length === 0};
}

export function fieldError(field, value, required) {
  const validation = field.validation || {};
  if (isEmptyValue(value)) return required ? {code: field.type === "consent" ? "consent_required" : "required"} : null;
  const kind = answerKind(field);
  if (field.type === "email" && !EMAIL.test(String(value))) return {code: "email"};
  if (field.type === "phone" && !/^\+?\d{8,15}$/.test(String(value))) return {code: "phone"};
  if (kind === "number") {
    if (typeof value !== "number" || Number.isNaN(value)) return {code: "number"};
    if (validation.min !== undefined && validation.min !== null && validation.min !== "" && value < Number(validation.min)) return {code: "min", n: Number(validation.min)};
    if (validation.max !== undefined && validation.max !== null && validation.max !== "" && value > Number(validation.max)) return {code: "max", n: Number(validation.max)};
    if (field.type === "rating") {
      const scale = Number(field.scale) || 5;
      if (value < 1 || value > scale || !Number.isInteger(value)) return {code: "rating", n: scale};
    }
  }
  if (kind === "date" && typeof value === "number" && Number.isNaN(value)) return {code: "date"};
  if (kind === "text") {
    const length = String(value).length;
    if (validation.minLength && length < Number(validation.minLength)) return {code: "min_length", n: Number(validation.minLength)};
    const max = Number(validation.maxLength) || (field.type === "long_text" ? 5000 : 500);
    if (length > max) return {code: "max_length", n: max};
  }
  if (kind === "choice" || kind === "multi") {
    const allowed = field.type === "yes_no" ? ["yes", "no"] : (field.options || []).map((option) => String(option.value));
    const list = Array.isArray(value) ? value : [value];
    if (list.some((item) => !allowed.includes(String(item)))) return {code: "option"};
    if (kind === "multi") {
      if (validation.minChoices && list.length < Number(validation.minChoices)) return {code: "min_choices", n: Number(validation.minChoices)};
      if (validation.maxChoices && list.length > Number(validation.maxChoices)) return {code: "max_choices", n: Number(validation.maxChoices)};
    }
  }
  if (kind === "file") {
    const max = Number(validation.maxFiles) || 3;
    if (value.length > max) return {code: "max_files", n: max};
  }
  return null;
}

// ---------------------------------------------------------------- scores
// Several independent dimensions; an option (or a number) gives points per dimension, times the question's weight.
// Hidden, empty or invalid answers give nothing (unless a dimension counts empty as 0). Bands must not overlap.
export function computeScores(definition, answers) {
  const dimensions = (definition && definition.scoreDimensions) || [];
  const fields = (definition && definition.fields) || [];
  const result = {};
  dimensions.forEach((dimension) => {
    let raw = 0;
    let min = 0;
    let max = 0;
    let counted = 0;
    fields.forEach((field) => {
      const weight = Number(field.scoreWeights && field.scoreWeights[dimension.key]);
      const factor = Number.isFinite(weight) && weight > 0 ? weight : 1;
      const points = optionPoints(field, dimension.key);
      if (!points) return;
      const value = answers ? answers[field.key] : undefined;
      const got = isEmptyValue(value) ? null : points.of(value);
      // an unanswered (hidden / skipped / invalid) question is left out of the scale too, so it never pulls the
      // score down — unless the dimension counts an empty answer as 0
      if (got === null && !dimension.emptyAsZero) return;
      const list = points.values;
      min += (field.type === "multi_choice" ? Math.min(0, ...list.filter((p) => p < 0).concat([0])) : Math.min(...list)) * factor;
      max += (field.type === "multi_choice"
        ? (dimension.aggregate === "max" ? Math.max(...list) : list.filter((p) => p > 0).reduce((a, b) => a + b, 0))
        : Math.max(...list)) * factor;
      if (got === null) return;
      raw += got * factor;
      counted += 1;
    });
    const label = dimension.label || dimension.key;
    if (!counted && !dimension.emptyAsZero) {
      result[dimension.key] = {label, raw: null, normalized: null, min, max, counted, band: null, bandLabel: null};
      return;
    }
    const normalized = dimension.normalize !== false && max > min ? Math.max(0, Math.min(100, Math.round(((raw - min) / (max - min)) * 100))) : null;
    const value = normalized !== null ? normalized : raw;
    const band = (dimension.bands || []).find((item) => value >= Number(item.min) && value <= Number(item.max));
    result[dimension.key] = {label, raw, normalized, min, max, counted, band: band ? band.key : null, bandLabel: band ? band.label : null};
  });
  return result;
}

function optionPoints(field, dimensionKey) {
  if (field.type === "rating" && field.scoreDimensions && field.scoreDimensions.includes(dimensionKey)) {
    const scale = Number(field.scale) || 5;
    const values = Array.from({length: scale}, (_, i) => i + 1);
    return {values, of: (value) => (Number.isFinite(Number(value)) ? Number(value) : null)};
  }
  if (["single_choice", "dropdown", "multi_choice", "yes_no"].includes(field.type)) {
    const options = field.type === "yes_no" ? (field.options && field.options.length ? field.options : [{value: "yes"}, {value: "no"}]) : (field.options || []);
    const map = {};
    options.forEach((option) => {
      const points = option.scores ? Number(option.scores[dimensionKey]) : NaN;
      if (Number.isFinite(points)) map[String(option.value)] = points;
    });
    const values = Object.values(map);
    if (!values.length) return null;
    return {
      values,
      of: (value) => {
        if (Array.isArray(value)) {
          const got = value.map((item) => map[String(item)]).filter((item) => item !== undefined);
          if (!got.length) return 0;
          return got.reduce((a, b) => a + b, 0);
        }
        return map[String(value)] !== undefined ? map[String(value)] : null;
      },
    };
  }
  return null;
}

// ---------------------------------------------------------------- endings and safe URLs
export function safeRedirectUrl(value) {
  const text = String(value || "").trim();
  if (!/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(text)) return null;
  try {
    const url = new URL(text);
    if (!["http:", "https:"].includes(url.protocol)) return null;
    return url.toString();
  } catch {
    return null;
  }
}

// the one ending of a submission: an early end first, then ending rules by priority, else the default
export function resolveEnding(definition, answers, context, earlyEnd) {
  const endings = (definition && definition.endings) || {};
  const fallback = endings.default || {type: "message", title: "", message: ""};
  const pick = (ending, key) => ({key: key || "default", ...sanitizeEnding(ending || fallback)});
  if (earlyEnd && earlyEnd.endingKey) {
    const target = (endings.rules || []).find((rule) => rule.key === earlyEnd.endingKey);
    if (target) return pick(target.ending, target.key);
  }
  for (const rule of byPriority((endings.rules || []).filter((item) => item && item.enabled !== false))) {
    if (evaluateCondition(rule.when, answers, context)) return pick(rule.ending, rule.key);
  }
  return pick(fallback, "default");
}

function sanitizeEnding(ending) {
  const type = ["message", "redirect", "page"].includes(ending.type) ? ending.type : "message";
  const text = (value, max) => String(value || "").replace(/<[^>]*>/g, "").slice(0, max);
  const out = {type, title: text(ending.title, 120), message: text(ending.message, 1000)};
  if (type === "redirect") {
    out.url = safeRedirectUrl(ending.url);
    out.newTab = Boolean(ending.newTab);
    if (!out.url) {
      out.type = "message";
    }
  }
  if (type === "page") {
    out.buttonLabel = text(ending.buttonLabel, 60);
    out.buttonUrl = safeRedirectUrl(ending.buttonUrl);
  }
  return out;
}

// ---------------------------------------------------------------- template variables
export function renderTemplate(template, variables) {
  return String(template || "").replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, name) => {
    const value = variables[name];
    return value === undefined || value === null ? "" : Array.isArray(value) ? value.join("\u060C ") : String(value);
  });
}

export function templateVariablesOf(template) {
  const out = [];
  String(template || "").replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, name) => { out.push(name); return ""; });
  return out;
}

function knownVariable(definition, name) {
  if (TEMPLATE_VARIABLES.includes(name)) return true;
  const fieldKeys = ((definition && definition.fields) || []).map((field) => field.key);
  const dims = ((definition && definition.scoreDimensions) || []).map((dimension) => dimension.key);
  const answer = /^answers\.(.+)$/.exec(name);
  if (answer) return fieldKeys.includes(answer[1]);
  const score = /^scores\.([^.]+)\.(normalized|band|raw)$/.exec(name);
  return Boolean(score && dims.includes(score[1]));
}

// ---------------------------------------------------------------- publish readiness
// {errors, warnings, notes}; each item {code, params, path}: the UI translates forms.report.<code> with the params
// and can open the place to fix.
export function validateDefinition(definition, options) {
  const errors = [];
  const warnings = [];
  const notes = [];
  const add = (list, code, params, path) => list.push({code, params: params || {}, path: path || null});
  const fields = (definition && definition.fields) || [];
  const meta = (definition && definition.meta) || {};
  if (!String(meta.title || "").trim()) add(errors, "title_empty", null, "meta.title");
  const answerable = fields.filter(isAnswerable).filter((field) => field.type !== "hidden");
  if (!answerable.length) add(errors, "no_questions", null, "fields");
  const keys = new Set();
  fields.forEach((field, index) => {
    const path = `fields.${field.key || index}`;
    if (!field.key || !/^[a-z][a-z0-9_]{0,40}$/.test(field.key)) add(errors, "field_key_invalid", {n: index + 1}, path);
    if (keys.has(field.key)) add(errors, "field_key_duplicate", {name: field.key}, path);
    keys.add(field.key);
    if (!FIELD_TYPES[field.type]) add(errors, "field_type_invalid", {name: field.label || field.key}, path);
    if (isAnswerable(field) && field.type !== "hidden" && !String(field.label || "").trim()) add(errors, "field_label_empty", {n: index + 1}, path);
    if (FIELD_TYPES[field.type] && FIELD_TYPES[field.type].options) {
      const options = field.options || [];
      if (options.length < 1) add(errors, "options_empty", {name: field.label || field.key}, path);
      const values = new Set();
      options.forEach((option) => {
        if (!String(option.value || "").trim()) add(errors, "option_value_empty", {name: field.label || field.key}, path);
        if (values.has(String(option.value))) add(errors, "option_value_duplicate", {name: field.label || field.key}, path);
        values.add(String(option.value));
      });
    }
    if (field.sensitive) add(warnings, "sensitive_field", {name: field.label || field.key}, path);
  });
  const fieldMap = {};
  fields.forEach((field) => { fieldMap[field.key] = field; });
  const checkCondition = (condition, path, depth) => {
    if (!condition) return;
    if (Array.isArray(condition.conditions)) {
      if (depth > MAX_RULE_DEPTH) add(errors, "rule_too_deep", {n: MAX_RULE_DEPTH}, path);
      condition.conditions.forEach((item) => checkCondition(item, path, depth + 1));
      return;
    }
    if (condition.source === "identity" || condition.source === "system") return;
    if (condition.source === "score") {
      if (!((definition.scoreDimensions || []).some((dimension) => dimension.key === condition.field))) add(errors, "rule_score_missing", null, path);
      return;
    }
    const field = fieldMap[condition.field];
    if (!field || !isAnswerable(field)) {
      add(errors, "rule_field_missing", null, path);
      return;
    }
    const allowed = OPERATORS[answerKind(field)] || [];
    if (!allowed.includes(condition.operator)) add(errors, "rule_operator_invalid", {name: field.label || field.key}, path);
    if (["is", "is_not", "includes_any", "includes_all", "includes_none"].includes(condition.operator) && FIELD_TYPES[field.type].options) {
      const values = (field.options || []).map((option) => String(option.value));
      const expected = Array.isArray(condition.value) ? condition.value : [condition.value];
      if (expected.some((value) => !values.includes(String(value)))) add(errors, "rule_option_missing", {name: field.label || field.key}, path);
    }
  };
  // in-fill rules: references, operators and show/hide cycles
  const edges = {};
  (definition.rules || []).forEach((rule, index) => {
    const path = `rules.${rule.key || index}`;
    checkCondition(rule.when, path, 1);
    (rule.then || []).forEach((action) => {
      if (!IN_FILL_ACTIONS.includes(action.type)) add(errors, "rule_action_invalid", null, path);
      if (action.type !== "end_form" && !fieldMap[action.field]) add(errors, "rule_target_missing", null, path);
      if (action.type === "end_form" && action.endingKey && !((definition.endings && definition.endings.rules) || []).some((item) => item.key === action.endingKey)) add(errors, "rule_ending_missing", null, path);
      conditionFields(rule.when).forEach((source) => {
        if (action.field) {
          edges[source] = edges[source] || new Set();
          edges[source].add(action.field);
        }
      });
    });
  });
  fields.forEach((field) => {
    if (field.mapping && field.mapping.target && !CUSTOMER_FIELDS.includes(field.mapping.target)) add(errors, "mapping_target_invalid", {name: field.label || field.key}, `fields.${field.key}`);
    if (field.mapping && field.mapping.policy && !MAPPING_POLICIES.includes(field.mapping.policy)) add(errors, "mapping_policy_invalid", {name: field.label || field.key}, `fields.${field.key}`);
  });
  if (hasCycle(edges)) add(errors, "rule_cycle", null, "rules");
  // destination
  const destination = definition.destination || {type: "form_responses"};
  if (destination.type === "customer") {
    const hasIdentity = fields.some((field) => field.mapping && IDENTITY_FIELDS.includes(field.mapping.target));
    if (!hasIdentity) add(warnings, "customer_identity_weak", null, "destination");
    if (options && options.crmAvailable === false) add(errors, "customer_unavailable", null, "destination");
  }
  if (destination.type === "project" && !(destination.project && destination.project.projectKey)) add(errors, "project_missing", null, "destination");
  if (destination.type === "form_responses") add(notes, "responses_only", null, "destination");
  // post-submit rules
  (definition.postSubmitRules || []).forEach((rule, index) => {
    const path = `postSubmitRules.${rule.key || index}`;
    checkCondition(rule.when, path, 1);
    (rule.actions || []).forEach((action) => {
      if (!POST_SUBMIT_ACTIONS.includes(action.type)) add(errors, "action_invalid", null, path);
      const config = action.config || {};
      if (action.type === "create_task" && !(config.projectKey || (destination.project && destination.project.projectKey))) add(errors, "action_project_missing", null, path);
      if (action.type === "update_customer" && destination.type !== "customer") add(errors, "action_customer_without_destination", null, path);
      [config.title, config.description, config.message].forEach((template) => templateVariablesOf(template).forEach((name) => {
        if (!knownVariable(definition, name)) add(errors, "template_variable_unknown", {name}, path);
      }));
    });
  });
  // scores
  (definition.scoreDimensions || []).forEach((dimension) => {
    const bands = [...(dimension.bands || [])].sort((a, b) => Number(a.min) - Number(b.min));
    for (let i = 1; i < bands.length; i += 1) {
      if (Number(bands[i].min) <= Number(bands[i - 1].max)) add(errors, "band_overlap", {name: dimension.label || dimension.key}, `scoreDimensions.${dimension.key}`);
    }
  });
  // endings
  const endings = definition.endings || {};
  const checkEnding = (ending, path) => {
    if (!ending) return;
    if (ending.type === "redirect" && !safeRedirectUrl(ending.url)) add(errors, "ending_url_invalid", null, path);
    if (ending.type === "page" && ending.buttonUrl && !safeRedirectUrl(ending.buttonUrl)) add(errors, "ending_button_url_invalid", null, path);
  };
  checkEnding(endings.default, "endings.default");
  (endings.rules || []).forEach((rule, index) => {
    checkEnding(rule.ending, `endings.rules.${rule.key || index}`);
    checkCondition(rule.when, `endings.rules.${rule.key || index}`, 1);
  });
  if (!endings.default) add(warnings, "ending_default_missing", null, "endings.default");
  // access
  const access = definition.access || {};
  if (!["public", "internal", "both"].includes(access.mode || "public")) add(errors, "access_invalid", null, "access");
  if ((access.mode || "public") !== "internal" && !access.maxSubmissions && !access.expiresAt) add(warnings, "public_unlimited", null, "access");
  if (answerable.length > 30) add(notes, "many_questions", {n: answerable.length}, "fields");
  if (options && options.testSubmissions === 0) add(notes, "no_test_yet", null, null);
  return {errors, warnings, notes, ready: errors.length === 0};
}

function hasCycle(edges) {
  const state = {};
  const visit = (node) => {
    if (state[node] === 1) return true;
    if (state[node] === 2) return false;
    state[node] = 1;
    for (const next of edges[node] || []) if (visit(next)) return true;
    state[node] = 2;
    return false;
  };
  return Object.keys(edges).some((node) => visit(node));
}

// ---------------------------------------------------------------- a fresh definition
export function emptyDefinition(texts) {
  const text = texts || {};
  return {
    schemaVersion: SCHEMA_VERSION,
    meta: {title: text.title || "", description: "", locale: "fa-IR", showProgress: false, theme: {preset: "zofino"}},
    fields: [{key: "q1", type: "short_text", label: text.firstQuestion || "", required: true}],
    rules: [],
    scoreDimensions: [],
    destination: {type: "form_responses"},
    postSubmitRules: [],
    endings: {default: {type: "message", title: text.endingTitle || "", message: text.endingMessage || ""}, rules: []},
    access: {mode: "public", allowMultiple: true, maxSubmissions: null, expiresAt: null},
  };
}

// the public, minimal view of a version: no destination, mapping, post-submit rules or internal ids
export function publicDefinition(definition) {
  const fields = ((definition && definition.fields) || []).map((field) => {
    const out = {key: field.key, type: field.type, label: field.label, help: field.help || "", placeholder: field.placeholder || "", required: Boolean(field.required)};
    if (field.options) out.options = field.options.map((option) => ({value: option.value, label: option.label}));
    if (field.validation) out.validation = field.validation;
    if (field.scale) out.scale = field.scale;
    if (field.scaleLabels) out.scaleLabels = field.scaleLabels;
    if (field.consentText) out.consentText = field.consentText;
    return out;
  });
  return {
    schemaVersion: SCHEMA_VERSION,
    meta: definition.meta || {},
    fields,
    // in-fill rules are needed to show / hide questions on the page; they only reference questions of the form
    rules: (definition.rules || []).filter((rule) => rule.enabled !== false).map((rule) => ({key: rule.key, priority: rule.priority, when: rule.when, then: rule.then, stopProcessing: rule.stopProcessing})),
  };
}
